import { useState, useEffect } from "react";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import { TeamAPI } from "../../services/api";
import Popup from "../../components/common/Popup";
import iconLogo from "../../assets/icon-logo.png";
import banner1 from "../../assets/banner-1.png";
import CheckedImg from "../../assets/Checked.png";
import UnCheckedImg from "../../assets/UnChecked.png";

interface TeamMember {
  id: number;
  user_id: number;
  member_position: string;
  full_name: string;
  personal_email: string;
  user?: {
    id: number;
    full_name: string;
    email: string;
  };
}

interface TeamStatusCheckpoint {
  name: string;
  completed: boolean;
}

interface Team {
  id: number;
  team_id: string;
  team_name: string;
  team_story?: string;
  league: {
    id: number;
    name: string;
  };
  category: {
    id: number;
    name: string;
  };
  members: TeamMember[];
  year: number;
  team_status: string;
  payment_status: string;
  profile_status: string;
  acknowledgment_status: string;
  member_count: number;
  is_complete: boolean;
  requirements: {
    met: number;
    total: number;
    needed: string[];
    all_met: boolean;
  };
  statusCheckpoints: {
    [key: string]: TeamStatusCheckpoint;
  };
  can_proceed_to_payment: boolean;
  user_position: string;
  created_at: string;
  updated_at: string;
}

interface TeamsResponse {
  success: boolean;
  teams: Team[];
  total_teams: number;
  can_create_more: boolean;
}

export default function TeamDashboard() {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [canCreateMore, setCanCreateMore] = useState(false);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [showMobileTips, setShowMobileTips] = useState(false);
  const [showEarlyBirdPopup, setShowEarlyBirdPopup] = useState(false);

  // Banner carousel data
  const banners = [
    {
      id: 1,
      image: banner1,
      title: "Vietnam Young Lions 2026",
      subtitle: "Unleash Your Creative Potential",
    },
    {
      id: 2,
      image: banner1, // Using same image as placeholder
      title: "Join the Competition",
      subtitle: "Make Your Mark in the Industry",
    },
    {
      id: 3,
      image: banner1, // Using same image as placeholder
      title: "Creative Excellence",
      subtitle: "Where Ideas Come to Life",
    },
  ];

  // Auto-advance banner carousel every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBannerIndex((prevIndex) => (prevIndex + 1) % banners.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [banners.length]);

  const loadTeams = async () => {
    try {
      setLoading(true);
      setError("");

      const response: TeamsResponse = await TeamAPI.getMyTeams();

      if (response.success) {
        setTeams(response.teams || []);
        setCanCreateMore(response.can_create_more || false);
      } else {
        setError("Failed to load teams");
      }
    } catch (err: any) {
      setError(err.message || "Failed to load teams");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTeams();
  }, []);

  // Status icon component
  const StatusIcon = ({ status }: { status: boolean }) => (
    <div className={`w-5 flex items-center justify-center `}>
      {status ? (
        <img src={CheckedImg} className="w-full" />
      ) : (
        <img src={UnCheckedImg} className="w-full" />
      )}
    </div>
  );

  // Team card component
  const TeamCard = ({ team }: { team: Team }) => {
    const handleViewDetails = () => {
      // Navigate to team details page
      window.location.href = `/dashboard/team/${team.team_id}`;
    };

    // Process status checkpoints from API
    const statusCheckpoints = Object.entries(team.statusCheckpoints || {});
    const completedCheckpoints = statusCheckpoints.filter(
      ([_, checkpoint]) => checkpoint.completed
    );
    const pendingCheckpoints = statusCheckpoints.filter(
      ([_, checkpoint]) => !checkpoint.completed
    );
    const allCompleted =
      completedCheckpoints.length === statusCheckpoints.length;

    return (
      <div className="bg-white border-2 border-[#262423] rounded-lg p-4 sm:p-6 mb-4 shadow-sm hover:shadow-md transition-shadow">
        {/* Two Column Layout - Responsive */}
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 mb-4">
          {/* Left Column - 40% on desktop, full width on mobile */}
          <div className="w-full sm:w-2/5 sm:pr-6 text-left">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
              {team.team_name}
            </h3>
            <p className="text-xs sm:text-sm text-gray-500 font-mono mb-3 sm:mb-4 tracking-wider">
              {team.team_id}
            </p>
            <hr className="border-gray-300 mb-3 sm:mb-4 w-full sm:w-1/2" />
            {/* Team Members */}
            <div className="space-y-2">
              {team.members.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center space-x-2 text-xs sm:text-sm text-gray-700"
                >
                  <svg
                    className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="truncate">
                    {member.full_name || member.user?.full_name}
                  </span>
                </div>
              ))}

              {/* Show placeholder for missing members */}
              {team.member_count < 2 && (
                <div className="flex items-center space-x-2 text-xs sm:text-sm text-gray-400">
                  <svg
                    className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="truncate">Waiting for team member...</span>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - 60% on desktop, full width on mobile */}
          <div className="w-full sm:w-3/5 text-left">
            {/* All Completed State */}
            {allCompleted ? (
              // <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              //   <div className="flex items-center space-x-3 mb-3">
              //     <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
              //       <svg
              //         className="w-5 h-5 text-green-600"
              //         fill="currentColor"
              //         viewBox="0 0 20 20"
              //       >
              //         <path
              //           fillRule="evenodd"
              //           d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              //           clipRule="evenodd"
              //         />
              //       </svg>
              //     </div>
              //     <div>
              //       <h4 className="text-sm font-semibold text-green-800">
              //         Team Complete!
              //       </h4>
              //       <p className="text-xs text-green-600">
              //         All requirements have been met and payment is complete.
              //       </p>
              //     </div>
              //   </div>
              //   <div className="text-xs text-green-700">
              //     <p className="font-medium">
              //       Competition Status: Ready to Compete
              //     </p>
              //     <p>Good luck in Vietnam Young Lions 2026!</p>
              //   </div>
              // </div>
              <>
                <div className="mb-4">
                  <div className="flex items-center space-x-2">
                    <StatusIcon status={true} />
                    <span className="text-xs sm:text-sm font-medium text-gray-800">
                      {completedCheckpoints.length} Requirements met
                    </span>
                  </div>
                </div>
                <div className="mb-4">
                  <div className="flex items-center space-x-2">
                    <StatusIcon status={false} />
                    <span className="text-xs sm:text-sm font-medium text-gray-800">
                      0 Requirements need your attention
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Requirements Met Section */}
                {completedCheckpoints.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center space-x-2">
                      <StatusIcon status={true} />
                      <span className="text-xs sm:text-sm font-medium text-gray-800">
                        {completedCheckpoints.length} Requirements met
                      </span>
                    </div>
                  </div>
                )}

                {/* Requirements Need Attention Section */}
                {pendingCheckpoints.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center space-x-2">
                      <StatusIcon status={false} />
                      <span className="text-xs sm:text-sm font-medium text-gray-800">
                        {pendingCheckpoints.length} Requirements need your
                        attention
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}
            {/* 
            <div className="mt-4 sm:mt-6">
              <p className="text-xs sm:text-sm text-gray-600 italic">
                You are eligible for Early Bird discounts,{" "}
                <button
                  onClick={() => setShowEarlyBirdPopup(true)}
                  className="underline text-gray-900 hover:text-gray-700 font-medium"
                >
                  read more
                </button>
              </p>
            </div> */}
          </div>
        </div>

        {/* Action Button */}
        <div className="flex justify-end pt-3 sm:pt-4 border-t border-gray-100">
          <button
            onClick={handleViewDetails}
            className="text-[#403634] hover:text-[#2d2520] py-2 px-4 sm:px-6 rounded-lg hover:bg-gray-50 transition-all font-medium text-xs sm:text-sm border border-[#403634] hover:border-[#2d2520] min-w-[120px] sm:min-w-[140px]"
          >
            View Team Details
          </button>
        </div>
      </div>
    );
  };

  // Tips content based on user's team status
  const getTipsContent = () => {
    if (teams.length === 0) {
      return {
        title: "Tips for a better adventure",
        items: [
          "Team name must be unique and not duplicated in leagues and categories.",
          "Team name should start and end with the name of a constellation that you like.",
          "Do not use numbers and special characters for team name.",
          "Copy and share the team ID with your teammate, then wait for their confirmation to complete your 2-member team.",
        ],
      };
    }

    if (canCreateMore) {
      return {
        title: "Tips for a better adventure",
        items: [
          "Team name must be unique and not duplicated in leagues and categories",
          "Team name should start and end with the name of a constellation that you like",
          "Do not use numbers and special characters for team name",
          "Copy and share team id with teammates and wait for confirmation to complete your 2-member team",
        ],
      };
    }

    return {
      title: "Tips for a better adventure",
      items: [
        "Fields marked * are required.",
        "Complete all team requirements to proceed to payment",
        "Both team members must confirm their participation",
        "Contact support if you need help with your team setup",
      ],
    };
  };

  const tips = getTipsContent();

  return (
    <DashboardLayout>
      <div className="flex flex-col lg:flex-row gap-8 bg-[#EDEDED] rounded-lg p-4 sm:p-6 lg:p-8 shadow-sm">
        {/* Main Content */}
        <div className="flex-1">
          <div className="mb-6">
            {/* Status message */}
            {teams.length > 0 && (
              <p className="text-sm text-gray-600 italic mb-6 text-left">
                {canCreateMore
                  ? `You have ${teams.length} team${
                      teams.length > 1 ? "s" : ""
                    } and can create ${2 - teams.length} more.`
                  : "You already have the maximum number of teams and cannot create more."}
              </p>
            )}
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm mb-6">
              {error}
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && teams.length === 0 && (
            <div className="text-center py-12">
              <img
                src={iconLogo}
                alt="VYL Logo"
                className="w-16 h-16 mx-auto mb-4 opacity-50"
              />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No teams yet
              </h3>
              <p className="text-gray-600 mb-6">
                Get started by creating your first team for the competition.
              </p>
              <button
                onClick={() =>
                  (window.location.href = "/dashboard/registration")
                }
                className="bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white py-3 px-6 rounded-lg hover:opacity-90 transition-opacity font-medium"
              >
                Create Your First Team
              </button>
            </div>
          )}

          {/* Teams List */}
          {!loading && !error && teams.length > 0 && (
            <div className="space-y-4">
              {teams.map((team) => (
                <TeamCard key={team.id} team={team} />
              ))}

              {/* Create More Teams Button */}
              {canCreateMore && (
                <div className="bg-white border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <img
                    src={iconLogo}
                    alt="VYL Logo"
                    className="w-12 h-12 mx-auto mb-3 opacity-50"
                  />
                  <p className="text-gray-600 mb-4">
                    You can create {2 - teams.length} more team
                    {2 - teams.length > 1 ? "s" : ""}
                  </p>
                  <button
                    onClick={() =>
                      (window.location.href = "/dashboard/registration")
                    }
                    className="bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white py-2 px-6 rounded-lg hover:opacity-90 transition-opacity font-medium"
                  >
                    Create New Team
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Sidebar - Tips and Promotional */}
        <div className="hidden lg:block w-80 space-y-4">
          {/* Tips Section */}
          <div className="bg-white rounded-lg p-6 shadow-sm">
            {/* Banner Carousel */}
            <div className="rounded-lg text-white relative overflow-hidden mb-5">
              <div className="relative w-full h-48">
                {banners.map((banner, index) => (
                  <div
                    key={banner.id}
                    className={`absolute inset-0 transition-opacity duration-500 ease-in-out ${
                      index === currentBannerIndex ? "opacity-100" : "opacity-0"
                    }`}
                  >
                    <img
                      src={banner.image}
                      alt={banner.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}

                {/* Carousel Indicators */}
                <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1">
                  {banners.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentBannerIndex(index)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        index === currentBannerIndex
                          ? "bg-white"
                          : "bg-white bg-opacity-50"
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {tips.title}
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              Fields marked <span className="text-red-500">*</span> are
              required.
            </p>
            <div className="space-y-2 text-xs text-gray-600">
              {tips.items.map((tip, index) => (
                <p key={index}>{tip}</p>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Tips Modal */}
      <div className="lg:hidden fixed bottom-4 right-4">
        <button
          onClick={() => setShowMobileTips(true)}
          className="bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-colors"
        >
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>

      {/* Mobile Tips Modal Popup */}
      {showMobileTips && (
        <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-sm w-full max-h-[80vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                {tips.title}
              </h3>
              <button
                onClick={() => setShowMobileTips(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <svg
                  className="w-5 h-5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>

            {/* Banner Carousel in Mobile Modal */}
            <div className="p-4">
              <div className="rounded-lg text-white relative overflow-hidden mb-4">
                <div className="relative w-full h-32">
                  {banners.map((banner, index) => (
                    <div
                      key={banner.id}
                      className={`absolute inset-0 transition-opacity duration-500 ease-in-out ${
                        index === currentBannerIndex
                          ? "opacity-100"
                          : "opacity-0"
                      }`}
                    >
                      <img
                        src={banner.image}
                        alt={banner.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-30 flex flex-col justify-end p-3">
                        <h4 className="text-sm font-bold text-white mb-1">
                          {banner.title}
                        </h4>
                        <p className="text-xs text-white opacity-90">
                          {banner.subtitle}
                        </p>
                      </div>
                    </div>
                  ))}

                  {/* Carousel Indicators for Mobile */}
                  <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 flex space-x-1">
                    {banners.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentBannerIndex(index)}
                        className={`w-1.5 h-1.5 rounded-full transition-colors ${
                          index === currentBannerIndex
                            ? "bg-white"
                            : "bg-white bg-opacity-50"
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Tips Content */}
              <p className="text-sm text-gray-600 mb-3">
                Fields marked <span className="text-red-500">*</span> are
                required.
              </p>
              <div className="space-y-3 text-sm text-gray-600">
                {tips.items.map((tip, index) => (
                  <p key={index}>{tip}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Early Bird Popup */}
      <Popup
        isOpen={showEarlyBirdPopup}
        onClose={() => setShowEarlyBirdPopup(false)}
        title="Early Bird Discounts"
        mode="info"
      >
        <div className="space-y-4">
          <div>
            <h3 className="font-semibold mb-2">Heading something</h3>
            <p>
              Sed ut perspiciatis unde omnis iste natus error sit voluptatem
              accusantium doloremque laudantium, totam rem aperiam, eaque ipsa
              quae ab illo inventore veritatis et quasi architecto beatae vitae
              dicta sunt explicabo. Nemo enim ipsam voluptatem quia voluptas sit
              aspernatur aut odit aut fugit, sed quia consequuntur magni dolores
              eos qui ratione voluptatem sequi nesciunt. Neque porro quisquam
              est, qui dolorem ipsum quia dolor sit amet, consectetur, adipisci
              velit, sed quia non numquam eius modi tempora incidunt ut labore
              et dolore magnam aliquam quaerat voluptatem.
            </p>
          </div>

          <div>
            <h3 className="font-semibold mb-2">Heading something</h3>
            <p>
              At vero eos et accusamus et iusto odio dignissimos ducimus qui
              blanditiis praesentium voluptatum deleniti atque corrupti quos
              dolores et quas molestias excepturi sint occaecati cupiditate non
              provident, similique sunt in culpa qui officia deserunt mollitia
              animi, id est laborum et dolorum fuga. Et harum quidem rerum
              facilis est et expedita distinctio. Nam libero tempore, cum soluta
              nobis est eligendi optio cumque nihil impedit quo minus id quod
              maxime placeat facere possimus, omnis voluptas assumenda est,
              omnis dolor repellendus. Temporibus autem quibusdam et aut
              officiis debitis aut rerum necessitatibus
            </p>
          </div>

          <div>
            <h3 className="font-semibold mb-2">Early Bird Benefits Include:</h3>
            <ul className="list-disc list-inside space-y-1">
              <li>20% discount on registration fees</li>
              <li>Priority access to workshops and mentoring sessions</li>
              <li>Exclusive networking events with industry leaders</li>
              <li>Early access to competition materials and briefs</li>
              <li>Special recognition in event publications</li>
            </ul>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-sm text-yellow-800">
              <strong>Important:</strong> Early Bird pricing is available until
              March 31st, 2026. Don't miss this opportunity to save on your
              Vietnam Young Lions experience!
            </p>
          </div>
        </div>
      </Popup>
    </DashboardLayout>
  );
}
